import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  HStack,
  IconButton,
  Image,
  Progress,
  Spinner,
} from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { ERoute } from 'types/frontend';

import useOCR from './useOCR';
import DevSandboxTooltip from '../../DevSandboxTooltip';
import { IProjectCard } from '../../types';

interface IExtractTextTabProps {
  isFreeUser: boolean;
  entityType?: string;
  designScreenshotUrl?: string | null;
  projectCards?: IProjectCard[];
}

const ExtractTextTab = ({
  isFreeUser,
  entityType,
  designScreenshotUrl,
  projectCards,
}: IExtractTextTabProps) => {
  const { isFree } = useCurrentUser();
  const { toastSuccess } = useToastWrapper();
  const { recognize, isLoading, progress, error, terminate } = useOCR();

  const cardsWithScreenshots = projectCards?.filter((c) => c.screenshot_url) || [];
  const isProject = entityType === 'project' && cardsWithScreenshots.length > 0;
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  // Cache extracted text per image URL
  const cacheRef = useRef<Record<string, string>>({});
  const [extractedText, setExtractedText] = useState<string | null>(null);

  const currentScreenshot = isProject
    ? cardsWithScreenshots[currentCardIndex]?.screenshot_url
    : designScreenshotUrl;
  const currentCardTitle = isProject ? cardsWithScreenshots[currentCardIndex]?.title : null;

  // Auto-extract when tab opens or card changes
  useEffect(() => {
    if (!currentScreenshot) {
      setExtractedText(null);
      return;
    }

    if (currentScreenshot in cacheRef.current) {
      setExtractedText(cacheRef.current[currentScreenshot]);
      return;
    }

    let stale = false;

    const extract = async () => {
      const result = await recognize(currentScreenshot);
      if (stale) return;
      if (result) {
        cacheRef.current[currentScreenshot] = result.text;
        setExtractedText(result.text);
      }
    };
    extract();

    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreenshot]);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopyText = useCallback(() => {
    if (extractedText) {
      navigator.clipboard.writeText(extractedText);
      toastSuccess('Extracted text copied to clipboard');
    }
  }, [extractedText, toastSuccess]);

  const handlePrevCard = useCallback(() => {
    if (isProject && currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
    }
  }, [isProject, currentCardIndex]);

  const handleNextCard = useCallback(() => {
    if (isProject && currentCardIndex < cardsWithScreenshots.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    }
  }, [isProject, cardsWithScreenshots.length, currentCardIndex]);

  if (isFreeUser || isFree) {
    return (
      <Box>
        <Box mb={4}>
          <Text fontSize="large" fontWeight="semibold" mb={2}>
            Extract Text
          </Text>
          <Text fontSize="small" opacity="secondary">
            Extract text content from design screenshots using OCR.
          </Text>
        </Box>
        <Box
          p={3}
          borderRadius="md"
          bgGradient="linear(to-r, purple.900, blue.900)"
          borderWidth="1px"
          borderColor="purple.600"
        >
          <Text fontSize="small" fontWeight="semibold" mb={1}>
            Extract text from designs
          </Text>
          <Text fontSize="micro" opacity="secondary" mb={3}>
            Use OCR to automatically extract text content from your design screenshots. Perfect for
            copying headings, labels, and body text directly into your code.
          </Text>
          <Button
            scheme="gradient"
            size="sm"
            to={ERoute.Plans}
            leftIcon={<Icon type="Rocket" />}
            width="100%"
          >
            Upgrade to BIG to extract text
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Text fontSize="large" fontWeight="semibold" mb={2}>
          Extract Text
        </Text>
        <Text fontSize="small" opacity="secondary">
          Automatically extracts text content from design screenshots using OCR.
        </Text>
      </Box>

      {/* Card navigation for projects */}
      {isProject && (
        <HStack justifyContent="space-between" alignItems="center" mb={3}>
          <HStack spacing={2}>
            <DevSandboxTooltip label="Previous card">
              <IconButton
                aria-label="Previous card"
                icon={<ChevronLeftIcon />}
                size="sm"
                variant="ghost"
                isDisabled={currentCardIndex === 0}
                onClick={handlePrevCard}
              />
            </DevSandboxTooltip>
            <Text fontSize="small" fontWeight="semibold">
              Card {currentCardIndex + 1} of {cardsWithScreenshots.length}
            </Text>
            <DevSandboxTooltip label="Next card">
              <IconButton
                aria-label="Next card"
                icon={<ChevronRightIcon />}
                size="sm"
                variant="ghost"
                isDisabled={currentCardIndex === cardsWithScreenshots.length - 1}
                onClick={handleNextCard}
              />
            </DevSandboxTooltip>
          </HStack>
          {currentCardTitle && (
            <Text fontSize="small" opacity="secondary" isTruncated maxW="200px">
              {currentCardTitle}
            </Text>
          )}
        </HStack>
      )}

      {/* Design preview */}
      {currentScreenshot ? (
        <Box mb={4} minH="120px">
          <Image
            src={currentScreenshot}
            alt={currentCardTitle || 'Design screenshot'}
            width="100%"
            height="auto"
            maxH="200px"
            objectFit="contain"
            borderRadius="md"
            bg="whiteAlpha.50"
            fallback={
              <Center height="120px" bg="whiteAlpha.100" borderRadius="md">
                <Text opacity="secondary">Image not found</Text>
              </Center>
            }
          />
        </Box>
      ) : (
        <Center height="120px" bg="whiteAlpha.100" borderRadius="md" mb={4}>
          <Text opacity="secondary">No design screenshot available</Text>
        </Center>
      )}

      {/* Card thumbnails for quick navigation */}
      {isProject && cardsWithScreenshots.length > 1 && (
        <Box mb={4}>
          <Text fontSize="small" fontWeight="semibold" mb={2}>
            All Cards
          </Text>
          <HStack spacing={2} overflowX="auto" pb={2}>
            {cardsWithScreenshots.map((card, index) => (
              <Box
                key={card.id}
                width="60px"
                height="45px"
                borderRadius="sm"
                overflow="hidden"
                cursor="pointer"
                opacity={index === currentCardIndex ? 1 : 0.5}
                borderWidth="2px"
                borderColor={index === currentCardIndex ? 'purple.400' : 'transparent'}
                transition="all 0.15s"
                onClick={() => setCurrentCardIndex(index)}
                _hover={{ opacity: 1 }}
                flexShrink={0}
              >
                <Image
                  src={card.screenshot_url || undefined}
                  alt={card.title}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  fallback={
                    <Center height="100%" bg="whiteAlpha.100">
                      <Text fontSize="micro">{index + 1}</Text>
                    </Center>
                  }
                />
              </Box>
            ))}
          </HStack>
        </Box>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <Box mb={4}>
          <HStack spacing={2} mb={2}>
            <Spinner size="xs" color="purple.300" />
            <Text fontSize="small" opacity="secondary">
              Extracting text... {progress}%
            </Text>
          </HStack>
          <Progress
            value={progress}
            size="xs"
            colorScheme="purple"
            borderRadius="full"
            bg="whiteAlpha.100"
          />
        </Box>
      )}

      {/* Error display */}
      {error && (
        <Box
          mb={4}
          p={3}
          borderRadius="md"
          bg="red.900"
          borderWidth="1px"
          borderColor="red.600"
        >
          <Text fontSize="small" color="red.200">
            {error}
          </Text>
        </Box>
      )}

      {/* Extracted text result */}
      {extractedText !== null && (
        <Box>
          <HStack alignItems="center" mb={2} spacing={2}>
            <Text fontSize="small" fontWeight="semibold">
              Extracted Text
            </Text>
            {extractedText && (
              <DevSandboxTooltip label="Copy all text">
                <IconButton
                  aria-label="Copy extracted text"
                  icon={<Icon type="Copy" size={14} />}
                  size="xs"
                  variant="ghost"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  onClick={handleCopyText}
                />
              </DevSandboxTooltip>
            )}
          </HStack>
          <Box
            p={3}
            borderRadius="md"
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.100"
            maxH="300px"
            overflowY="auto"
          >
            {extractedText ? (
              <Box
                as="pre"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                style={{ fontSize: '13px', lineHeight: '1.6', fontFamily: 'inherit' }}
                color="whiteAlpha.900"
              >
                {extractedText}
              </Box>
            ) : (
              <Text fontSize="small" opacity="tertiary">
                No text found in this image.
              </Text>
            )}
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default ExtractTextTab;
